import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, IconButton, InputAdornment, Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';

// step 'id'    -> กรอกเลขบัตรประชาชน
// step 'reset' -> แสดงชื่อพนักงาน + username + ตั้งรหัสผ่านใหม่
export default function ForgotPasswordDialog({ open, onClose, onDone }) {
  const [step, setStep] = useState('id');
  const [taxId, setTaxId] = useState('');
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState(null); // { username, employeeName, resetToken }
  const [pwd, setPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('id');
      setTaxId('');
      setAccount(null);
      setPwd('');
      setConfirmPwd('');
      setShowPwd(false);
      setLoading(false);
    }
  }, [open]);

  const digits = taxId.replace(/\D/g, '');

  const handleLookup = async () => {
    if (digits.length !== 13) {
      showAlert('กรุณากรอกเลขบัตรประชาชน 13 หลักให้ถูกต้อง');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`${process.env.REACT_APP_API_URL}/user/forgot-password`, { tax_id: digits });
      setAccount(data);
      setStep('reset');
    } catch {
      // ข้อความ error โชว์โดย global error dialog แล้ว
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!pwd || pwd.length < 4) { showAlert('รหัสผ่านใหม่ต้องมีอย่างน้อย 4 ตัวอักษร'); return; }
    if (pwd !== confirmPwd) { showAlert('รหัสผ่านไม่ตรงกัน'); return; }
    setLoading(true);
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/user/reset-password`, {
        token: account?.resetToken,
        password: pwd,
      });
      showAlert('ตั้งรหัสผ่านใหม่สำเร็จ กรุณาเข้าสู่ระบบด้วยรหัสผ่านใหม่');
      onDone?.(account?.username || '');
      onClose?.();
    } catch {
      // global error dialog
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {step === 'id' ? 'ลืมรหัสผ่าน' : 'ตั้งรหัสผ่านใหม่'}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {step === 'id' ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              กรอกเลขบัตรประชาชนของคุณเพื่อยืนยันตัวตน
            </Typography>
            <TextField
              label="เลขบัตรประชาชน"
              fullWidth
              autoFocus
              value={taxId}
              onChange={(e) => setTaxId(e.target.value.replace(/[^\d\s-]/g, '').slice(0, 17))}
              inputProps={{ inputMode: 'numeric' }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
              helperText={`${digits.length}/13 หลัก`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeOutlinedIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">พนักงาน</Typography>
              <Typography variant="body1" fontWeight={700}>{account?.employeeName || '-'}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Username</Typography>
              <Typography variant="body2" fontWeight={600}>{account?.username || '-'}</Typography>
            </Box>
            <TextField
              label="รหัสผ่านใหม่"
              type={showPwd ? 'text' : 'password'}
              fullWidth
              autoFocus
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockResetIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPwd((v) => !v)}>
                      {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="ยืนยันรหัสผ่านใหม่"
              type={showPwd ? 'text' : 'password'}
              fullWidth
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleReset(); }}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>ยกเลิก</Button>
        {step === 'id' ? (
          <Button variant="contained" onClick={handleLookup} disabled={loading || digits.length !== 13}>
            {loading ? 'กำลังตรวจสอบ...' : 'ถัดไป'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleReset} disabled={loading || !pwd || pwd !== confirmPwd}>
            {loading ? 'กำลังบันทึก...' : 'บันทึกรหัสผ่านใหม่'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
